import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PortalIcon from './PortalIcon';
import PortalPaymentModal from './PortalPaymentModal';
import {
  externalTrackingUrl,
  formatDate,
  formatMoney,
  orderPaymentLabel,
  orderPaymentPending,
  orderProcessLabel,
  statusTone,
  openAuthenticatedPdf,
  openAuthenticatedPdfPost,
} from '../lib/portal';

function Badge({ tone, children }) {
  return <span className={`portal-badge portal-badge--${tone}`}>{children}</span>;
}

export default function PortalPedidos() {
  const { authFetch, authRequestJson } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [query, setQuery] = useState('');
  const [processFilter, setProcessFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [payTarget, setPayTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [copying, setCopying] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authRequestJson('/portal/pedidos?take=200');
      setOrders(Array.isArray(response?.data) ? response.data : []);
      setError('');
    } catch (requestError) {
      setError(requestError.message || 'No se pudieron cargar los pedidos.');
    } finally {
      setLoading(false);
    }
  }, [authRequestJson]);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return orders.filter((order) => {
      const matchesQuery = !needle || [order.tiendaNubeOrderNumber, order.tiendaNubeOrderId, order.contactName, order.contactEmail, order.localidadDestino]
        .some((value) => String(value || '').toLowerCase().includes(needle));
      const process = orderProcessLabel(order);
      const matchesProcess = processFilter === 'all' || (processFilter === 'pending' ? process === 'Pendiente de procesar' : process !== 'Pendiente de procesar');
      const pending = orderPaymentPending(order);
      const matchesPayment = paymentFilter === 'all' || (paymentFilter === 'pending' ? pending : !pending);
      return matchesQuery && matchesProcess && matchesPayment;
    });
  }, [orders, paymentFilter, processFilter, query]);

  const pendingOrders = useMemo(
    () => filtered.filter((order) => orderPaymentPending(order)),
    [filtered]
  );

  const paidOrders = useMemo(
    () => filtered.filter((order) => !orderPaymentPending(order)),
    [filtered]
  );

  const allPendingSelected = pendingOrders.length > 0 && pendingOrders.every((order) => selectedIds.has(order.id));
  const allPaidSelected = paidOrders.length > 0 && paidOrders.every((order) => selectedIds.has(order.id));

  const toggleSelection = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllPending = () => {
    if (allPendingSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pendingOrders.forEach((order) => next.delete(order.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        pendingOrders.forEach((order) => next.add(order.id));
        return next;
      });
    }
  };

  const toggleAllPaid = () => {
    if (allPaidSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        paidOrders.forEach((order) => next.delete(order.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        paidOrders.forEach((order) => next.add(order.id));
        return next;
      });
    }
  };

  const selectedPendingOrders = useMemo(
    () => pendingOrders.filter((order) => selectedIds.has(order.id)),
    [pendingOrders, selectedIds]
  );

  const selectedPaidOrders = useMemo(
    () => paidOrders.filter((order) => selectedIds.has(order.id)),
    [paidOrders, selectedIds]
  );

  const printLabel = async (order) => {
    setBusyId(order.id);
    setError('');
    try {
      await openAuthenticatedPdf(authFetch, `/portal/pedidos/${order.id}/etiqueta.pdf`);
    } catch (requestError) {
      setError(requestError.message || 'No se pudo abrir el rótulo.');
    } finally {
      setBusyId(null);
    }
  };

  const printSelectedLabels = async () => {
    if (selectedPaidOrders.length === 0) {
      setError('Selecciona al menos un pedido abonado para imprimir los rótulos.');
      return;
    }
    setPrinting(true);
    setError('');
    try {
      await openAuthenticatedPdfPost(authFetch, '/portal/pedidos/etiquetas.pdf', {
        pedidoIds: selectedPaidOrders.map((order) => order.id),
      });
    } catch (requestError) {
      setError(requestError.message || 'No se pudo abrir el archivo de rótulos.');
    } finally {
      setPrinting(false);
    }
  };

  const copyPaymentLink = async () => {
    if (selectedPendingOrders.length === 0) {
      setError('Selecciona al menos un pedido pendiente de pago.');
      return;
    }
    setCopying(true);
    setError('');
    try {
      const response = await authRequestJson('/portal/pagos/enlace', {
        method: 'POST',
        body: JSON.stringify({ pedidoIds: selectedPendingOrders.map((order) => order.id) }),
      });
      const enlace = response?.enlacePago;
      if (!enlace) {
        throw new Error(response?.error || 'No se pudo generar el enlace de pago.');
      }
      await navigator.clipboard.writeText(enlace);
      setMessage(`Enlace de pago copiado (${selectedPendingOrders.length} pedido${selectedPendingOrders.length === 1 ? '' : 's'}).`);
      window.setTimeout(() => setMessage(''), 4000);
    } catch (requestError) {
      setError(requestError.message || 'No se pudo copiar el enlace de pago.');
    } finally {
      setCopying(false);
    }
  };

  const syncOrders = async () => {
    setSyncing(true);
    setError('');
    setMessage('');
    try {
      const response = await authRequestJson('/portal/pedidos/sync', { method: 'POST' });
      const { sincronizados, creados, actualizados } = response || {};
      setMessage(
        `Sincronizacion completa: ${sincronizados || 0} pedido(s), ${creados || 0} nuevo(s), ${actualizados || 0} actualizado(s).`
      );
      await load();
      window.setTimeout(() => setMessage(''), 6000);
    } catch (requestError) {
      setError(requestError.message || 'No se pudo sincronizar los pedidos.');
    } finally {
      setSyncing(false);
    }
  };

  const paymentBody = useMemo(
    () => (payTarget ? { pedidoIds: payTarget.map((order) => order.id) } : null),
    [payTarget]
  );

  const paymentAmount = useMemo(
    () => (payTarget ? payTarget.reduce((sum, order) => sum + (order.valorViaje || 0), 0) : 0),
    [payTarget]
  );

  return (
    <div className="portal-app">
      <div className="portal-wrap">
        <header className="portal-page-header--split">
          <div><p className="portal-kicker">Operación / Pedidos</p><h1>Todos tus pedidos</h1><p>Seguimiento, pago y rotulado de cada envío recibido desde Tiendanube.</p></div>
          <div className="portal-header-actions">
            <button type="button" className="portal-btn portal-btn--outline" disabled={syncing} onClick={() => void syncOrders()}>
              <PortalIcon name="refresh" size={17} /> {syncing ? 'Sincronizando…' : 'Sincronizar'}
            </button>
            <Link className="portal-btn" to="/colectas/nueva"><PortalIcon name="truck" size={17} /> Solicitar colecta</Link>
          </div>
        </header>

        {error ? <div className="portal-alert portal-alert--error">{error}</div> : null}
        {message ? <div className="portal-alert portal-alert--success">{message}</div> : null}

        <div className="portal-filterbar">
          <label className="portal-search"><PortalIcon name="search" size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar por número, cliente o localidad" /></label>
          <select className="portal-control portal-filter-select" value={processFilter} onChange={(event) => setProcessFilter(event.target.value)} aria-label="Filtrar procesamiento">
            <option value="all">Todo procesamiento</option><option value="pending">Pendientes de procesar</option><option value="done">Procesados / listos</option>
          </select>
          <select className="portal-control portal-filter-select" value={paymentFilter} onChange={(event) => setPaymentFilter(event.target.value)} aria-label="Filtrar pago">
            <option value="all">Todo pago</option><option value="pending">Flete pendiente</option><option value="paid">Flete abonado</option>
          </select>
        </div>

        <div className="portal-bulk-actions">
          <div className="portal-bulk-group">
            <button
              type="button"
              className="portal-btn portal-btn--mint"
              disabled={selectedPendingOrders.length === 0}
              onClick={() => setPayTarget(selectedPendingOrders)}
            >
              <PortalIcon name="card" size={16} /> Pagar {selectedPendingOrders.length > 0 ? `(${selectedPendingOrders.length})` : ''}
            </button>
            <button
              type="button"
              className="portal-btn portal-btn--outline"
              disabled={selectedPendingOrders.length === 0 || copying}
              onClick={() => void copyPaymentLink()}
            >
              <PortalIcon name="link" size={16} /> {copying ? 'Copiando…' : `Copiar enlace${selectedPendingOrders.length > 0 ? ` (${selectedPendingOrders.length})` : ''}`}
            </button>
          </div>
          <button
            type="button"
            className="portal-btn portal-btn--outline"
            disabled={selectedPaidOrders.length === 0 || printing}
            onClick={() => void printSelectedLabels()}
          >
            <PortalIcon name="download" size={16} /> {printing ? 'Preparando…' : `Imprimir rótulos${selectedPaidOrders.length > 0 ? ` (${selectedPaidOrders.length})` : ''}`}
          </button>
        </div>

        <section className="portal-card portal-table-card">
          {loading ? <div className="portal-loading">Cargando pedidos…</div> : null}
          {!loading && filtered.length === 0 ? <div className="portal-empty"><strong>No encontramos pedidos con esos filtros.</strong><p>Probá cambiar la búsqueda o esperá la próxima sincronización de Tiendanube.</p></div> : null}
          {!loading && filtered.length > 0 ? (
            <div className="portal-table-wrap">
              <table className="portal-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={allPendingSelected}
                        onChange={toggleAllPending}
                        title="Seleccionar pedidos pendientes de pago"
                        aria-label="Seleccionar pedidos pendientes de pago"
                      />
                    </th>
                    <th>Pedido</th>
                    <th>Destino</th>
                    <th>Procesamiento</th>
                    <th>Pago del flete</th>
                    <th>Importe</th>
                    <th>Seguimiento</th>
                    <th>Acciones</th>
                    <th>
                      <input
                        type="checkbox"
                        checked={allPaidSelected}
                        onChange={toggleAllPaid}
                        title="Seleccionar pedidos abonados"
                        aria-label="Seleccionar pedidos abonados"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((order) => {
                    const process = orderProcessLabel(order);
                    const pending = orderPaymentPending(order);
                    const trackingUrl = order.trackingUrl || externalTrackingUrl(order.trackingNumber);
                    return (
                      <tr key={order.id} data-clickable="true" onClick={() => navigate(`/pedidos/${order.id}`)}>
                        <td onClick={(event) => event.stopPropagation()}>
                          {pending ? (
                            <input
                              type="checkbox"
                              checked={selectedIds.has(order.id)}
                              onChange={() => toggleSelection(order.id)}
                              aria-label={`Seleccionar pedido ${order.tiendaNubeOrderNumber || order.tiendaNubeOrderId}`}
                            />
                          ) : null}
                        </td>
                        <td><strong>#{order.tiendaNubeOrderNumber || order.tiendaNubeOrderId}</strong><span className="portal-muted">{formatDate(order.actualizadoEn)}</span></td>
                        <td>{order.localidadDestino || 'Destino pendiente'}<span className="portal-muted">{order.contactName || order.contactEmail || 'Cliente Tiendanube'}</span></td>
                        <td><Badge tone={statusTone(process)}>{process}</Badge></td>
                        <td><Badge tone={statusTone(order.cobroEstado)}>{orderPaymentLabel(order)}</Badge></td>
                        <td>{pending ? formatMoney(order.valorViaje) : formatMoney(order.cobroMonto || order.valorViaje)}</td>
                        <td>{order.trackingNumber ? <a className="portal-inline-link" href={trackingUrl} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>{order.trackingNumber}</a> : <span className="portal-muted">Pendiente</span>}</td>
                        <td>
                          <div className="portal-actions">
                            <button type="button" className="portal-btn portal-btn--outline portal-btn--small" onClick={(event) => { event.stopPropagation(); navigate(`/pedidos/${order.id}`); }} title="Ver detalle"><PortalIcon name="arrow" size={14} /></button>
                            {pending ? (
                              <button type="button" className="portal-btn portal-btn--mint portal-btn--small" onClick={(event) => { event.stopPropagation(); setPayTarget([order]); }} title="Pagar flete">Pagar</button>
                            ) : null}
                            <button
                              type="button"
                              className="portal-btn portal-btn--outline portal-btn--small"
                              disabled={busyId === order.id || pending}
                              onClick={(event) => { event.stopPropagation(); void printLabel(order); }}
                              title={pending ? 'Aboná el flete para imprimir el rótulo' : 'Imprimir rótulo'}
                            >
                              <PortalIcon name="download" size={14} />
                            </button>
                          </div>
                        </td>
                        <td onClick={(event) => event.stopPropagation()}>
                          {!pending ? (
                            <input
                              type="checkbox"
                              checked={selectedIds.has(order.id)}
                              onChange={() => toggleSelection(order.id)}
                              aria-label={`Seleccionar pedido ${order.tiendaNubeOrderNumber || order.tiendaNubeOrderId}`}
                            />
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>

        <p className="portal-muted" style={{ marginTop: 12 }}>{filtered.length} pedido{filtered.length === 1 ? '' : 's'} visible{filtered.length === 1 ? '' : 's'}.</p>
      </div>

      {payTarget ? (
        <PortalPaymentModal
          title={`Pagar ${payTarget.length === 1 ? `pedido #${payTarget[0].tiendaNubeOrderNumber || payTarget[0].tiendaNubeOrderId}` : `${payTarget.length} pedidos`}`}
          amount={paymentAmount}
          preferencePath="/portal/pagos/preferencia"
          body={paymentBody}
          onClose={() => { setPayTarget(null); void load(); }}
        />
      ) : null}
    </div>
  );
}
